import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useToast, Link, Center } from "@chakra-ui/react"
import useMemberStore from "@/store/MemberStore"
import useNotificationSubscriptionStore from "@/store/NotificationSubscriptionStore"
import useKeywordDetailsStore from "@/store/KeywordDetailsStore"
import useKeywordNotificationListStore from "@/store/KeywordNotificationListStore"
import { motion, AnimatePresence } from "framer-motion"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { TimePicker } from "@/components/ui/time-picker"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Loader } from "@/components/ui/loader"
import NotificationChannelList from "@/components/NotificationChannels/NotificationChannelList"
import PlanInsufficientAlert from "@/components/Common/PlanInsufficientAlert"

interface KeywordNotificationSettingsFormProps {
  keywordId: string
  onSuccess?: () => void
}

export default function KeywordNotificationSettingsForm({
  keywordId,
  onSuccess,
}: KeywordNotificationSettingsFormProps) {
  const toast = useToast()
  const { getKeywordTrackingMinimumInterval } = useMemberStore()
  const { fetchKeywordDetails } = useKeywordDetailsStore()
  const { fetchKeywordNotifications } = useKeywordNotificationListStore()
  const [showLimitAlert, setShowLimitAlert] = useState(false)
  const [showFrequencyLimitAlert, setShowFrequencyLimitAlert] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [notifyInterval, setNotifyInterval] = useState(
    getKeywordTrackingMinimumInterval() / 3600
  )
  const [notificationMethod, setNotificationMethod] = useState<
    "interval" | "specific_times"
  >("interval")
  const [specificTimes, setSpecificTimes] = useState<string[]>([])

  const AVAILABLE_NOTIFY_INTERVALS = [1, 3, 6, 8, 12, 24]

  const {
    findSubscription,
    createSubscription,
    updateSubscription,
  } = useNotificationSubscriptionStore()
  const [subscription, setSubscription] = useState<any>(null)
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([])

  const form = useForm({
    defaultValues: {
      enableNotification: false,
      keywordName: "",
      notifyInterval: getKeywordTrackingMinimumInterval() / 3600,
      notificationMethod: "interval" as "interval" | "specific_times",
    },
  })

  useEffect(() => {
    const loadData = async () => {
      if (keywordId) {
        setIsLoading(true)
        try {
          await fetchKeywordDetails(keywordId)
          const result = await findSubscription("newPostNotification", keywordId)

          const currentKeywordDetails =
            useKeywordDetailsStore.getState().keywordDetails

          const hasTimeConstraints =
            result.success && result.data?.configuration?.timeConstraints
          const method = hasTimeConstraints ? "specific_times" : "interval"

          form.reset({
            enableNotification:
              result.success && result.data ? result.data.isActive : false,
            keywordName: currentKeywordDetails?.keyword || "",
            notifyInterval:
              result.success && result.data?.configuration?.interval
                ? result.data.configuration.interval / 3600
                : getKeywordTrackingMinimumInterval() / 3600,
            notificationMethod: method,
          })

          setNotificationMethod(method)
          if (
            hasTimeConstraints &&
            result.data?.configuration?.timeConstraints?.schedule?.weekday?.times
          ) {
            setSpecificTimes(
              result.data.configuration.timeConstraints.schedule.weekday.times
            )
          }

          if (result.success && result.data) {
            setSubscription(result.data)
            setSelectedChannelIds(result.data.channels.map((c: any) => c.id))
            if (result.data.configuration?.interval) {
              setNotifyInterval(result.data.configuration.interval / 3600)
            }
          }
        } finally {
          setIsLoading(false)
        }
      }
    }
    loadData()
  }, [
    keywordId,
    findSubscription,
    form,
    fetchKeywordDetails,
    getKeywordTrackingMinimumInterval,
  ])

  const onSubmit = async () => {
    if (!keywordId) {
      return
    }

    try {
      const isEnabled = form.getValues("enableNotification")

      let configuration: any = { id: keywordId }
      if (isEnabled) {
        if (notificationMethod === "interval") {
          configuration.interval = notifyInterval * 3600
        } else {
          configuration.interval = 300
          configuration.timeConstraints = {
            schedule: {
              weekday: {
                times: specificTimes,
              },
            },
            timezone: "Asia/Taipei",
          }
        }
      }

      if (!subscription && isEnabled) {
        const result = await createSubscription({
          featureCode: "newPostNotification",
          configuration,
          channels: selectedChannelIds.map((channelId) => ({ channelId })),
        })

        if (!result.success && result.error) {
          const errorCode = (result.error as any).error
          if (errorCode === 1523) {
            setShowLimitAlert(true)
            return
          } else if (errorCode === 1522) {
            setShowFrequencyLimitAlert(true)
            return
          }
        }

        if (result.success) {
          toast({
            title: "設定已更新",
            description: "通知設定已成功儲存",
            status: "success",
            duration: 5000,
            isClosable: true,
          })
          const newResult = await findSubscription("newPostNotification", keywordId)
          if (newResult.success && newResult.data) {
            setSubscription(newResult.data)
          }
          fetchKeywordNotifications()
          onSuccess?.()
        }
      } else if (subscription) {
        const updateResult = await updateSubscription(subscription.id, {
          configuration: isEnabled ? configuration : subscription.configuration,
          channels: isEnabled
            ? selectedChannelIds.map((channelId) => ({ channelId }))
            : subscription.channels.map((channel: any) => ({
                channelId: channel.id,
              })),
          isActive: isEnabled,
        })

        if (updateResult.success) {
          toast({
            title: "設定已更新",
            description: isEnabled ? "通知設定已成功儲存" : "通知已停用",
            status: "success",
            duration: 5000,
            isClosable: true,
          })
          const newResult = await findSubscription("newPostNotification", keywordId)
          if (newResult.success && newResult.data) {
            setSubscription(newResult.data)
          }
          fetchKeywordNotifications()
          onSuccess?.()
        }
      }
    } catch (error) {
      toast({
        title: "更新失敗",
        description: "無法儲存設定",
        status: "error",
        duration: 5000,
        isClosable: true,
      })
    }
  }

  if (isLoading) {
    return (
      <Center py={8}>
        <Loader className="h-6 w-6" />
      </Center>
    )
  }

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="enableNotification"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">啟用通知</FormLabel>
                    <FormDescription>
                      開啟此功能以接收關鍵字通知
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="keywordName"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">關鍵字</FormLabel>
                    <FormDescription>
                      偵測到此關鍵字時會發送通知給您
                    </FormDescription>
                  </div>
                  <FormControl>
                    <span className="font-medium">{field.value}</span>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notificationMethod"
              render={({ field }) => (
                <FormItem className="rounded-lg border p-4">
                  <div className="space-y-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">通知方式</FormLabel>
                      <FormDescription>選擇接收通知的方式</FormDescription>
                    </div>
                    <FormControl>
                      <RadioGroup
                        value={notificationMethod}
                        onValueChange={(value: "interval" | "specific_times") => {
                          setNotificationMethod(value)
                          field.onChange(value)
                        }}
                        disabled={!form.watch("enableNotification")}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="interval" id="interval" />
                          <label
                            htmlFor="interval"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            依間隔時間通知
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="specific_times" id="specific_times" />
                          <label
                            htmlFor="specific_times"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            指定時間通知
                          </label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </div>
                </FormItem>
              )}
            />
            {notificationMethod === "interval" && (
              <FormField
                control={form.control}
                name="notifyInterval"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">通知間隔</FormLabel>
                      <FormDescription>設定接收通知的時間間隔</FormDescription>
                    </div>
                    <Select
                      value={notifyInterval.toString()}
                      onValueChange={(value) => {
                        setNotifyInterval(Number(value))
                        field.onChange(Number(value))
                      }}
                      disabled={!form.watch("enableNotification")}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="選擇間隔時間" />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_NOTIFY_INTERVALS.map((interval) => (
                          <SelectItem
                            key={interval}
                            value={interval.toString()}
                            disabled={
                              interval < getKeywordTrackingMinimumInterval() / 3600
                            }
                          >
                            {`${interval} 小時`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
            {notificationMethod === "specific_times" && (
              <div className="rounded-lg border p-4">
                <div className="space-y-4">
                  <div className="space-y-0.5">
                    <div className="text-base font-medium">指定通知時間</div>
                    <div className="text-sm text-muted-foreground">
                      選擇每天要接收通知的時間點
                    </div>
                  </div>
                  <TimePicker
                    selectedTimes={specificTimes}
                    onTimesChange={setSpecificTimes}
                    disabled={!form.watch("enableNotification")}
                  />
                </div>
              </div>
            )}
            {getKeywordTrackingMinimumInterval() > 3600 && (
              <FormDescription>
                最短通知發送間隔：{getKeywordTrackingMinimumInterval() / 3600}小時。
                <Link href="/pricing" className="text-blue-500" isExternal>
                  升級方案
                </Link>
                來縮短通知間隔時間
              </FormDescription>
            )}
            <AnimatePresence>
              {form.watch("enableNotification") && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <NotificationChannelList
                    featureCode="newPostNotification"
                    id={keywordId}
                    onChannelsChange={setSelectedChannelIds}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={
                  form.watch("enableNotification") &&
                  (selectedChannelIds.length === 0 ||
                    (notificationMethod === "specific_times" &&
                      specificTimes.length === 0))
                }
              >
                儲存
              </Button>
            </div>
          </div>
        </Form>
      </form>
      <PlanInsufficientAlert
        title="已達上限"
        message="新串文通知已達上限，升級方案可以設定更多！"
        isOpen={showLimitAlert}
        onOpenChange={setShowLimitAlert}
      />
      <PlanInsufficientAlert
        title="無法新增 LINE 通知"
        message="您的方案無法使用 LINE 通知，請升級方案！"
        isOpen={showFrequencyLimitAlert}
        onOpenChange={setShowFrequencyLimitAlert}
      />
    </>
  )
}
